import { Box, Grid, Stack, Typography } from "@mui/material";
import { memo, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { SearchInput } from "@4uhub/lib4uhub";
import { DashboardEvaluation } from "./DashboardEvaluation/DashboardEvaluation";
import { DashboardSortPopover } from "./DashboardSortPopover/DashboardSortPopover";
import { IDashBoardComponentProps, TOrder } from "./models";
import { DashboardPieChart } from "./DashboardPieChart/DashboardPieChart";
import { DashboardIcon } from "./DashboardIcon/DashboardIcon";
import { DashboardNoData } from "./DashboardNoData/DashboardNoData";
import { DashboardTable } from "./DashboardTable/DashboardTable";
import { IDashboardPieChart } from "./DashboardPieChart/models";
import { useDashboard } from "../../../../store/contexts/DashBoardContext";
import { MONEY_REGEX } from "../../../../utils/maks";
import { DashboardBarChart } from "./DashboardBarChart/DashboardBarChart";

const TRANSLATION_PATH = "components.panel.";

const DashBoardComponent: React.FC<IDashBoardComponentProps> = ({
  data,
  components,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { grouper } = data;

  const [search, setSearch] = useState("");

  const [order, setOrder] = useState<TOrder>("name");

  const { width } = useDashboard();

  const isArray =
    data.value &&
    JSON.parse(data.value).data &&
    Array.isArray(JSON.parse(data.value).data);

  const value = data.value ? JSON.parse(data.value) : null;

  const changeSortHandler = useCallback(
    (_: React.MouseEvent<HTMLElement, MouseEvent>, newValue: any) => {
      if (!newValue) return;
      setOrder(newValue);
    },
    [],
  );

  const avoidCodes = ["12", "32", "19", "33"];

  const listIndicatorsAvoidCodes = [...avoidCodes, "34"];

  const columns = width
    ? width <= 600
      ? 12
      : width <= 900
        ? 6
        : width <= 1156
          ? 4
          : 2.4
    : 3;

  return (
    <Grid
      item
      xs={avoidCodes.includes(data.code) ? 12 : columns}
      minWidth={150}
      padding={2}
      borderRadius={1}
      sx={
        grouper || data.code === "12"
          ? {
              width: "100%",
              height: "100%",
              maxHeight: "80vh",
              display: "flex",
              alignItems: "start",
              flexWrap: "wrap",
              gap: 2,
            }
          : undefined
      }
    >
      <Stack
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1}
          color={(t) => t.palette.grey[t.palette.mode === "light" ? 800 : 200]}
        >
          {(data.icon || data.code === "32") && (
            <DashboardIcon
              icon={data.icon}
              code={data.code}
              grouper={grouper}
            />
          )}
          <Typography sx={{ lineHeight: 1.2 }}>{data.name}</Typography>
        </Stack>
        {data.code === "12" && components && components.length > 0 && (
          <DashboardTable
            data={components
              .filter((c) => !listIndicatorsAvoidCodes.includes(c.code))
              .map((c) => ({
                name: c.name,
                value: c.value ? JSON.parse(c.value).data : 0,
              }))}
            columns={[
              {
                field: "name",
                headerName: `${t(TRANSLATION_PATH + "name")}`,
                flex: 1,
              },
              {
                field: "value",
                headerName: `${t(TRANSLATION_PATH + "value")}`,
                flex: 1,
              },
            ]}
          />
        )}
        {data.code === "32" && value && (
          <DashboardPieChart
            data={
              Object.keys(value).map((key) => ({
                id: key,
                value: value[key].percentage,
                label:
                  key === "human"
                    ? `${t(TRANSLATION_PATH + "attendant")} (${
                        value[key].count
                      })`
                    : key === "bot"
                      ? `Bot (${value[key].count})`
                      : key,
              })) as IDashboardPieChart[]
            }
          />
        )}
        {data.code === "33" && value && <DashboardBarChart data={value} />}
        {data.code === "19" && data.value && (
          <DashboardTable data={JSON.parse(data.value).items} />
        )}
        {!avoidCodes.includes(data.code) ? (
          data.value ? (
            JSON.parse(data.value).data && !isArray ? (
              <Box
                sx={(t) => ({
                  mt: 2,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: t.palette.background.default,
                  border: `1px solid ${t.palette.divider}`,
                  borderRadius: 2,
                })}
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: data.value ? 30 : 15 }}
                >
                  {grouper && !grouper.includes("TIME")
                    ? String(JSON.parse(data.value).data).replace(
                        MONEY_REGEX,
                        language === "en-US" ? "," : ".",
                      )
                    : JSON.parse(data.value).data}
                </Typography>
              </Box>
            ) : JSON.parse(data.value).data &&
              JSON.parse(data.value).data.length > 0 &&
              isArray ? (
              <Box
                sx={{
                  pt: 2,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <SearchInput
                    placeholder={t(`${TRANSLATION_PATH}search_placeholder`)}
                    onSearch={setSearch}
                  />
                  <DashboardSortPopover
                    value={order}
                    onChange={changeSortHandler}
                  />
                </Box>
                <Box
                  sx={{
                    pt: 2,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {JSON.parse(data.value)
                    .data.filter((d: any) =>
                      "name" in d && search.length > 0
                        ? d.name.toLowerCase().includes(search.toLowerCase())
                        : d,
                    )
                    .sort((a: any, b: any) => {
                      if (order === "name") return 0;
                      if (order === "average-asc")
                        return a.average < b.average ? -1 : 1;
                      if (order === "average-desc")
                        return a.average < b.average ? 1 : -1;
                      return 0;
                    })
                    .map((d: any) => (
                      <DashboardEvaluation data={d} key={d.id} />
                    ))}
                </Box>
              </Box>
            ) : (
              <DashboardNoData />
            )
          ) : (
            <DashboardNoData />
          )
        ) : null}
      </Stack>
    </Grid>
  );
};

export default memo(DashBoardComponent);
